/**
 * The email signature's MODEL and VALIDATION — what an operator fills in, and whether it is
 * acceptable. Shared by two consumers that must agree to the character:
 *
 * - the ops dashboard's generator (`apps/dashboard/src/lib/email-signature/`), which renders the
 *   signature and validates the form as it is typed, and
 * - the API (`internal-admin/email-signatures`), which stores the team's saved signatures and
 *   refuses one the generator could not render.
 *
 * Browser-safe on purpose — the dashboard reads it through `@easy-invoices/types/email-signature`,
 * so it may import nothing but `./company` (itself import-free). The RENDERING stays in the
 * dashboard: the server stores signatures, it never draws one. Design contract:
 * `docs/architecture/email-signatures.md`.
 */

import { COMPANY_IDENTITY } from './company';

/** Every language a signature can be written in. */
export const SIGNATURE_LOCALES = ['de', 'en'] as const;

/** The two languages a signature is written in. Independent of the dashboard's UI language. */
export type SignatureLocale = (typeof SIGNATURE_LOCALES)[number];

/** Everything an operator fills in. Company facts are NOT here — they come from `COMPANY_IDENTITY`. */
export interface SignatureInput {
  readonly fullName: string;
  readonly jobTitle: string;
  readonly email: string;
  readonly phone: string;
  readonly mobile: string;
  /** Optional HTTPS URL of a portrait. Shown in place of the logo when set. */
  readonly photoUrl: string;
  readonly locale: SignatureLocale;
  /** The logo square beside the name. The type-set wordmark is printed either way. */
  readonly showLogo: boolean;
  /** Legal name, address, representatives and USt-IdNr. */
  readonly showLegal: boolean;
}

/** The form fields that can fail validation. */
export type SignatureField =
  | 'fullName'
  | 'jobTitle'
  | 'email'
  | 'phone'
  | 'mobile'
  | 'photoUrl';

/** Why a field failed. The page maps each to a catalogue key. */
export type SignatureFieldError =
  | 'required'
  | 'tooLong'
  | 'invalidEmail'
  | 'invalidPhone'
  | 'invalidUrl';

/** Field → the first error it has. A field that is fine is absent. */
export type SignatureErrors = Partial<
  Record<SignatureField, SignatureFieldError>
>;

/** Upper bound per free-text field. Long enough for any real name or title, short enough to lay out. */
export const MAX_FIELD_LENGTH = 80;

/** Upper bound for the photo URL. */
export const MAX_URL_LENGTH = 500;

/** Upper bound for a saved signature's name — the label in the list, e.g. "Erik – Support (EN)". */
export const MAX_SIGNATURE_NAME_LENGTH = 80;

/**
 * The company's number in international form, derived from `COMPANY_IDENTITY.phone` so the two
 * cannot drift. The stored value is the Impressum's German `0AAAA/NNNN` form; a recipient abroad
 * needs `+49`. Anything not in that form is returned as stored.
 */
export function companyPhoneInternational(): string {
  const match = /^0(\d+)\/(\d+)$/.exec(COMPANY_IDENTITY.phone.trim());
  return match ? `+49 ${match[1]} ${match[2]}` : COMPANY_IDENTITY.phone;
}

/** A blank form, prefilled with what is known before the operator types anything. */
export function defaultSignatureInput(
  fullName = '',
  locale: SignatureLocale = 'de',
): SignatureInput {
  return {
    fullName,
    jobTitle: '',
    email: '',
    phone: companyPhoneInternational(),
    mobile: '',
    photoUrl: '',
    locale,
    showLogo: true,
    showLegal: true,
  };
}

/** Trims and collapses every run of whitespace — tabs and line breaks included — to one space. */
export function normaliseField(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * The `tel:` link for a number as a person types it, or null when it cannot be dialled.
 *
 * E.164 out: `+`, a non-zero country code, 7–15 digits in total. A national number (`02721 …`) is
 * read as GERMAN, because this team is — a number without a country code is otherwise undiallable
 * from abroad, which is where a signature's `tel:` link is most useful. The `(0)` of
 * `+49 (0) 2721 …` is the trunk prefix a caller from abroad must NOT dial, and is dropped.
 */
export function telHref(raw: string): string | null {
  const withoutTrunk = raw.replace(/\(\s*0\s*\)/g, '');
  const kept = withoutTrunk.replace(/[^\d+]/g, '');
  if (kept.lastIndexOf('+') > 0) return null;
  let e164: string;
  if (kept.startsWith('+')) e164 = kept;
  else if (kept.startsWith('00')) e164 = `+${kept.slice(2)}`;
  else if (kept.startsWith('0')) e164 = `+49${kept.slice(1)}`;
  else return null;
  return /^\+[1-9]\d{6,14}$/.test(e164) ? `tel:${e164}` : null;
}

const EMAIL_SHAPE = /^[^\s@<>"'(),;:]+@[^\s@<>"'(),;:]+\.[a-z]{2,}$/i;

/** Whether a URL is one the signature may point an image at: absolute HTTPS, nothing else. */
export function isHttpsUrl(raw: string): boolean {
  if (/[\s"'<>]/.test(raw)) return false;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && url.hostname.length > 0;
  } catch {
    return false;
  }
}

/** Validates a form. An empty result means it renders. */
export function validateSignatureInput(input: SignatureInput): SignatureErrors {
  const errors: SignatureErrors = {};
  const fullName = normaliseField(input.fullName);
  if (!fullName) errors.fullName = 'required';
  else if (fullName.length > MAX_FIELD_LENGTH) errors.fullName = 'tooLong';

  if (normaliseField(input.jobTitle).length > MAX_FIELD_LENGTH) {
    errors.jobTitle = 'tooLong';
  }

  const email = normaliseField(input.email);
  if (!email) errors.email = 'required';
  else if (email.length > MAX_FIELD_LENGTH) errors.email = 'tooLong';
  else if (!EMAIL_SHAPE.test(email)) errors.email = 'invalidEmail';

  for (const field of ['phone', 'mobile'] as const) {
    const value = normaliseField(input[field]);
    if (!value) continue;
    if (value.length > MAX_FIELD_LENGTH) errors[field] = 'tooLong';
    else if (!telHref(value)) errors[field] = 'invalidPhone';
  }

  const photoUrl = input.photoUrl.trim();
  if (photoUrl) {
    if (photoUrl.length > MAX_URL_LENGTH) errors.photoUrl = 'tooLong';
    else if (!isHttpsUrl(photoUrl)) errors.photoUrl = 'invalidUrl';
  }
  return errors;
}

/** Whether a form has no errors. */
export function isSignatureValid(input: SignatureInput): boolean {
  return Object.keys(validateSignatureInput(input)).length === 0;
}

/** Whether a value is one of {@link SIGNATURE_LOCALES}. */
export function isSignatureLocale(value: unknown): value is SignatureLocale {
  return (
    typeof value === 'string' &&
    (SIGNATURE_LOCALES as readonly string[]).includes(value)
  );
}

/**
 * Validates a saved signature's NAME — the label it is listed under, which is not part of the
 * rendered signature and therefore not in {@link validateSignatureInput}.
 *
 * @returns The error, or null when the name is acceptable.
 */
export function validateSignatureName(
  name: string,
): 'required' | 'tooLong' | null {
  const normalised = normaliseField(name);
  if (!normalised) return 'required';
  return normalised.length > MAX_SIGNATURE_NAME_LENGTH ? 'tooLong' : null;
}
